import * as _ from 'lodash'
import { network, docC } from '../globals'
import { Path } from '../lib/Path'
import { Cursor, CursorV } from '../lib/Cursor'
import * as Model from '../types/document'
import { blockInfo, BlockInfo } from 'src/model/blocks';

type NodeInfo = InputNodeInfo | GraphNodeInfo | SwitchOutputNodeInfo
export interface InputNodeInfo {
  type: 'input',
  path: Path,
  name: string,
  isHigher?: boolean,
}
export interface GraphNodeInfo {
  type: 'graph',
  path: Path,
  name: string,
  isHigher?: boolean,
  graphPath: Path,
  definingCallId: Model.CallId,
  usingCallId: Model.CallId | undefined,
  referredNodeId: Model.NodeId | undefined,
}
export interface SwitchOutputNodeInfo {
  type: 'switch-output',
  path: Path,
  name: string,
  isHigher?: boolean,
  switchPath: Path,
}

export const NodeInfo = {
  nodeC  (info: NodeInfo)             { return docC<Model.Node>  (info.path) },
  graphC (info: GraphNodeInfo)        { return docC<Model.Graph> (info.graphPath) },
  switchC(info: SwitchOutputNodeInfo) { return docC<Model.Switch>(info.switchPath) },
}

type NodeInfoTable = {[nodeId: string]: NodeInfo}

export const nodeInfo = network.valueFunc('nodeInfo', (blockC: Cursor<Model.Block>, nodeId: Model.NodeId): NodeInfo => {
  const result = nodeInfoTable(blockC)[nodeId]
  if (!result) {
    console.log("trouble getting", nodeId, "from", nodeInfoTable(blockC))
  }
  return result
}, _.isEqual)

export const nodeInfoTable = network.valueFunc('nodeInfoTable', (blockC: Cursor<Model.Block>): NodeInfoTable => {
  let selfInfo = {
    ...nodeInfoForInputs(blockC.select('inputs')),
    ...nodeInfoForSteps(blockC.select('steps')),
  }

  const parentBlockId = blockInfo(blockC._('id').get()).parentBlockId
  if (parentBlockId) {
    const parentBlockC = BlockInfo.blockC(blockInfo(parentBlockId))
    return {...selfInfo, ..._.mapValues(nodeInfoTable(parentBlockC), (info) => ({...info, isHigher: true}))}
  } else {
    return selfInfo
  }
})

const nodeInfoForInputs = network.valueFunc('nodeInfoForInputs', (inputsC: Cursor<Array<Model.Node>>): NodeInfoTable => {
  const nodeInfoTable = {}
  inputsC.fetch().call(CursorV.elems).forEach((inputC) => {
    const input = inputC.value()
    nodeInfoTable[input.id] = {
      type: 'input',
      path: inputC.path(),
      name: input.name,
    }
  })
  return nodeInfoTable
})

const nodeInfoForSteps = network.valueFunc('nodeInfoForSteps', (maybeStepsC: Cursor<Model.Steps | undefined>): NodeInfoTable => {
  const table = {}
  const stepsC = maybeStepsC.fetch().call(CursorV.unwrapMaybe)
  if (stepsC) {
    stepsC.call(CursorV.elemsKeyed, 'id').forEach((stepC) =>
      addNodeInfoForStep(table, stepC)
    )
  }
  return table
})

function addNodeInfoForStep(table: NodeInfoTable, stepC: CursorV<Model.Step>) {
  const step = stepC.value()

  switch (step.stepType) {
  case 'graph': {
    const graphC = stepC.unfetch() as Cursor<Model.Graph>
    graphC.select('nodes').fetch().call(CursorV.values).forEach((nodeC) => {
      const node = nodeC.value()
      const definingCallId = callIdDefiningNodeInGraph(graphC, node.id)
      const usingCallId = callIdUsingNodeInGraph(graphC, node.id)

      // Figure out if this graph-node is an identity call away from some other node
      let referredNodeId = undefined
      if (definingCallId) {
        const definingCall = graphC.select('calls').select(definingCallId).get()
        if (definingCall.callType === 'code') {
          const inputMatchingCode = _.find(definingCall.inputs, {inside: definingCall.code})
          if (inputMatchingCode) {
            referredNodeId = inputMatchingCode.outside
          }
        }
      }

      table[node.id] = {
        type: 'graph',
        path: nodeC.path(),
        graphPath: graphC.path(),
        definingCallId: definingCallId,
        usingCallId: usingCallId,
        name: node.name,
        referredNodeId
      }
    })
    // graphC.select('calls').fetch().call(CursorV.values).forEach((callC) => {
    //   const call = callC.value()
    //   if (call.callType === 'list-op') {
    //     const listOpC = callC.unfetch() as Cursor<Model.ListOpCall>
    //     const deeper = nodeInfoTable(listOpC.select('block'))
    //     console.log("recursing! adding ", deeper)
    //     Object.assign(table, nodeInfoTable(listOpC.select('block')))
    //     console.log("new table", table)
    //   }
    // })
    break }
  case 'switch': {
    const switchC = stepC.unfetch() as Cursor<Model.Switch>
    switchC.select('cases').fetch().call(CursorV.elems).forEach((caseCursor) => {
      Object.assign(table, nodeInfoForSteps(caseCursor.select('condition').unfetch()))
      Object.assign(table, nodeInfoForSteps(caseCursor.select('steps').unfetch()))
    })
    const outputNodeC = switchC.select('output')
    const outputNode = outputNodeC.get()
    table[outputNode.id] = {
      type: 'switch-output',
      path: outputNodeC.path(),
      switchPath: switchC.path(),
      name: outputNode.name,
    }
    break }
  case 'text':
    break
  }
}


export const referredOrOwnNodeId = network.valueFunc('referredOrOwnNodeId', (blockC: Cursor<Model.Block>, nodeId: Model.NodeId): Model.NodeId => {
  const info = nodeInfo(blockC, nodeId)
  if (info && info.type === 'graph' && info.referredNodeId) {
    return info.referredNodeId
  } else {
    return nodeId
  }
})


/// GRAPH STRUCTURE

const callIdDefiningNodeInGraphTable = network.valueFunc('callIdDefiningNodeInGraphTable', (graphC: Cursor<Model.Graph>) => {
  const graph = graphC.get()

  let callsDefiningNodes = {}
  _.forEach(graph.calls, (call) => {
    callsDefiningNodes[call.outputs[0].outside] = call.id
  })
  return callsDefiningNodes
})

const callIdDefiningNodeInGraph = network.valueFunc('callIdDefiningNodeInGraph', (graphPath: Cursor<Model.Graph>, nodeId: Model.NodeId) => {
  return callIdDefiningNodeInGraphTable(graphPath)[nodeId]
}, _.isEqual)

const callIdUsingNodeInGraphTable = network.valueFunc('callIdUsingNodeInGraphTable', (graphC: Cursor<Model.Graph>) => {
  const graph = graphC.get()

  let callsUsingNodes = {}
  _.forEach(graph.calls, (call) => {
    _.forEach(call.inputs, (input) => {
      callsUsingNodes[input.outside] = call.id
    })
  })
  return callsUsingNodes
})

const callIdUsingNodeInGraph = network.valueFunc('callIdUsingNodeInGraph', (graphPath: Cursor<Model.Graph>, nodeId: Model.NodeId) => {
  return callIdUsingNodeInGraphTable(graphPath)[nodeId]
}, _.isEqual)



// WEBPACK FOOTER //
// ./src/model/nodeInfo.ts